import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Clock, MapPin, GripVertical } from "lucide-react";
import { Button } from "./ui/button";
import api, { formatApiError } from "../lib/api";
import {
  DAY_BLOCKS,
  resolveTaskBlock,
  normalizeRanges,
  DEFAULT_DAY_BLOCK_RANGES,
} from "../lib/dayBlocks";
import { getWorkspace } from "../lib/workspaces";
import { cn } from "../lib/utils";
import { toast } from "sonner";

/**
 * DayBlocksView — calm planning surface that organises a single day into
 * four mini boards (Morning / Afternoon / Evening / Anytime). Designed for
 * ADHD / students / anxiety-sensitive users who prefer broad strokes over
 * rigid scheduling.
 *
 * Props:
 *  - tasks: array of all tasks (we filter by `selectedDate` here)
 *  - selectedDate: ISO YYYY-MM-DD
 *  - onSelectDate(iso): change which day is being viewed
 *  - onEditTask(task)
 *  - onCreateTask({ due_date, day_block }): open TaskModal pre-filled
 *  - refreshTasks(): re-fetch from server
 *  - ranges: user's customised day-block ranges (or DEFAULT_DAY_BLOCK_RANGES)
 */
export default function DayBlocksView({
  tasks,
  selectedDate,
  onSelectDate,
  onEditTask,
  onCreateTask,
  refreshTasks,
  ranges,
}) {
  const effectiveRanges = useMemo(
    () => normalizeRanges(ranges || DEFAULT_DAY_BLOCK_RANGES),
    [ranges]
  );

  // Tasks for the selected day, bucketed by block
  const groups = useMemo(() => {
    const byBlock = { morning: [], afternoon: [], evening: [], anytime: [] };
    for (const t of tasks) {
      if (!t.due_date) continue;
      if (t.due_date !== selectedDate) continue;
      const block = resolveTaskBlock(t, effectiveRanges);
      if (!block) {
        // tasks with neither block nor time → bucket as anytime
        byBlock.anytime.push(t);
      } else {
        byBlock[block].push(t);
      }
    }
    // Sort: explicit block_position wins (manual order), else by time, untimed last
    for (const key of Object.keys(byBlock)) {
      byBlock[key].sort((a, b) => {
        const ap = a.block_position;
        const bp = b.block_position;
        const aHas = typeof ap === "number";
        const bHas = typeof bp === "number";
        if (aHas && bHas) return ap - bp;
        if (aHas) return -1;
        if (bHas) return 1;
        const at = a.due_time || a.start_time || "99:99";
        const bt = b.due_time || b.start_time || "99:99";
        return at.localeCompare(bt);
      });
    }
    return byBlock;
  }, [tasks, selectedDate, effectiveRanges]);

  // ---- Date header ----
  const dt = useMemo(() => new Date(`${selectedDate}T00:00:00`), [selectedDate]);
  const navigate = (delta) => {
    const next = new Date(dt);
    next.setDate(next.getDate() + delta);
    const iso = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(
      next.getDate()
    ).padStart(2, "0")}`;
    onSelectDate(iso);
  };
  const todayIso = useMemo(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
  }, []);

  // ---- Drag-drop state ----
  const [draggingId, setDraggingId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  /**
   * Reorder tasks within a block, or move a task to a new block + position.
   *
   * @param targetBlock  block key being dropped into
   * @param targetTaskId task being inserted ABOVE (null = append to end)
   */
  const onDrop = async (targetBlock, targetTaskId = null) => {
    setDragOver(null);
    if (!draggingId) return;
    const moving = tasks.find((t) => t.id === draggingId);
    setDraggingId(null);
    if (!moving) return;

    // Build new ordered list of task IDs in the target block
    const others = (groups[targetBlock] || []).filter((t) => t.id !== draggingId);
    const insertAt =
      targetTaskId == null
        ? others.length
        : Math.max(0, others.findIndex((t) => t.id === targetTaskId));
    const newOrder = [
      ...others.slice(0, insertAt),
      moving,
      ...others.slice(insertAt),
    ];

    // Optimistic: snapshot current position so we don't fire a no-op
    const sourceBlock = resolveTaskBlock(moving, effectiveRanges) || "anytime";
    const sameBlock = sourceBlock === targetBlock;
    const sameSlot =
      sameBlock &&
      newOrder.findIndex((t) => t.id === draggingId) ===
        (groups[targetBlock] || []).findIndex((t) => t.id === draggingId);
    if (sameSlot) return;

    try {
      // Renumber positions 1..N for the affected block.
      // For a cross-block move we ALSO bump the source block's tasks so their
      // ordering remains compact (cheap — at most ~10 PUTs per drop).
      const targetUpdates = newOrder.map((t, idx) => {
        const patch = { block_position: idx + 1 };
        if (t.id === moving.id && !sameBlock) patch.day_block = targetBlock;
        return api.put(`/tasks/${t.id}`, patch);
      });
      const sourceUpdates = sameBlock
        ? []
        : (groups[sourceBlock] || [])
            .filter((t) => t.id !== draggingId)
            .map((t, idx) => api.put(`/tasks/${t.id}`, { block_position: idx + 1 }));

      await Promise.all([...targetUpdates, ...sourceUpdates]);
      if (!sameBlock) toast.success(`Moved to ${getBlockLabel(targetBlock)}`);
      refreshTasks?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't reorder");
    }
  };

  return (
    <div data-testid="day-blocks-view" className="anim-fade-up">
      {/* Date header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-1">
            Day Blocks
          </div>
          <h2
            data-testid="day-blocks-date"
            className="font-display text-2xl sm:text-3xl font-black tracking-tight"
          >
            {dt.toLocaleDateString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </h2>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="day-blocks-prev-btn"
          >
            <ChevronLeft size={15} />
          </Button>
          <Button
            variant="outline"
            onClick={() => onSelectDate(todayIso)}
            data-testid="day-blocks-today-btn"
            className="text-xs"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(1)}
            data-testid="day-blocks-next-btn"
          >
            <ChevronRight size={15} />
          </Button>
        </div>
      </div>

      {/* 4-column grid (2x2 on mobile, 4x1 on desktop) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {DAY_BLOCKS.map((block) => (
          <BlockColumn
            key={block.key}
            block={block}
            tasks={groups[block.key] || []}
            isDragOver={dragOver === block.key}
            onDragOver={() => setDragOver(block.key)}
            onDragLeave={() => setDragOver((v) => (v === block.key ? null : v))}
            onDrop={() => onDrop(block.key, null)}
            draggingId={draggingId}
            setDraggingId={setDraggingId}
            onDropOnTask={(targetTaskId) => onDrop(block.key, targetTaskId)}
            onCreate={() => onCreateTask({ due_date: selectedDate, day_block: block.key })}
            onEditTask={onEditTask}
            range={effectiveRanges[block.key]}
          />
        ))}
      </div>
    </div>
  );
}

function getBlockLabel(key) {
  return DAY_BLOCKS.find((b) => b.key === key)?.label || key;
}

/**
 * Format an "HH:MM" 24-hr string as a compact am/pm label.
 *   "07:00" → "7am"     "07:30" → "7:30am"
 *   "12:00" → "12pm"    "00:00" → "12am"     "24:00" → "12am"
 *   "23:45" → "11:45pm"
 */
function formatAmPm(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return "";
  if (hhmm === "24:00") return "12am";
  const [hStr, mStr = "00"] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h)) return hhmm;
  const ampm = h < 12 ? "am" : "pm";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  if (!m) return `${h12}${ampm}`;
  return `${h12}:${String(m).padStart(2, "0")}${ampm}`;
}

function BlockColumn({
  block,
  tasks,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  draggingId,
  setDraggingId,
  onDropOnTask,
  onCreate,
  onEditTask,
  range,
}) {
  const Icon = block.icon;
  return (
    <div
      data-testid={`day-block-${block.key}`}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      className={cn(
        "rounded-xl border bg-card p-3 transition-all duration-200 min-h-[180px] flex flex-col",
        isDragOver ? "border-primary/70 ring-2 ring-primary/30" : "border-border"
      )}
      style={{
        borderTop: `3px solid ${block.accent}`,
      }}
    >
      {/* Block header */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${block.accent}1A`, color: block.accent }}
          >
            <Icon size={14} strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <div className="font-display text-sm font-black tracking-tight flex items-center gap-1.5">
              <span aria-hidden="true">{block.emoji}</span>
              <span>{block.label}</span>
            </div>
            <div className="text-[10px] text-muted-foreground font-mono">
              {range
                ? `${formatAmPm(range.start)} – ${formatAmPm(range.end)}`
                : "no specific range"}
              {" · "}
              {tasks.length} {tasks.length === 1 ? "task" : "tasks"}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onCreate}
          aria-label={`Add task to ${block.label}`}
          data-testid={`day-block-add-${block.key}`}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition"
        >
          <Plus size={14} />
        </button>
      </div>

      {/* Task list */}
      <ul className="space-y-1.5 flex-1">
        {tasks.length === 0 ? (
          <li className="text-[11px] text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">
            Nothing here. Drag a task in, or tap + to add one.
          </li>
        ) : (
          tasks.map((t) => (
            <TaskChip
              key={t.id}
              task={t}
              dragging={draggingId === t.id}
              onDragStart={() => setDraggingId(t.id)}
              onDragEnd={() => setDraggingId(null)}
              onDropOnTask={() => onDropOnTask?.(t.id)}
              onClick={() => onEditTask?.(t)}
            />
          ))
        )}
      </ul>
    </div>
  );
}

function TaskChip({ task, dragging, onDragStart, onDragEnd, onDropOnTask, onClick }) {
  const wsColor = getWorkspace(task.dashboard || "personal").color;
  const time = task.due_time || task.start_time;
  const hasLocation = !!(task.location && (task.location.name || task.location.address));
  const isDone = task.status === "done";
  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        // Allow dropping ON this chip to reorder within / between blocks
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDropOnTask?.();
      }}
      onClick={onClick}
      data-testid={`day-block-task-${task.id}`}
      className={cn(
        "group flex items-center gap-2 rounded-md border border-border bg-secondary/30 px-2 py-1.5 cursor-pointer transition-all",
        "hover:border-primary/40 hover:bg-secondary/60",
        dragging && "opacity-40",
        isDone && "opacity-60"
      )}
      style={{ borderLeft: `3px solid ${wsColor}` }}
    >
      <GripVertical
        size={11}
        className="text-muted-foreground/50 group-hover:text-muted-foreground shrink-0"
      />
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "text-sm font-medium truncate leading-tight",
            isDone && "line-through"
          )}
        >
          {task.title}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
          {time && (
            <span className="inline-flex items-center gap-0.5 font-mono">
              <Clock size={9} /> {formatAmPm(time)}
            </span>
          )}
          {hasLocation && (
            <span
              className="inline-flex items-center gap-0.5"
              title={task.location?.name || task.location?.address}
            >
              <MapPin size={9} />
              <span className="max-w-[10ch] truncate">
                {task.location?.name || task.location?.address}
              </span>
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
